import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { secureGet, secureDelete } from "../../config/api";
import { PrimaryButton } from "../../components/PrimaryButton";
import { FamilyCard } from "../../components/Card";
import OptionsMenu from "../../components/OptionsMenu";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import LoadingLogo from "../../components/LoadingLogo";
import ModuleWrapper from "../../components/ModuleWrapper";
import Filter from "../../components/Filter";
import UserContext from '../../Shared/UserContext';
import SnackbarNotification from '../../components/SnackbarNotification';

import AddIcon from "../../icons/plusB.svg";
import EditIcon from "../../icons/edit.svg";
import DeleteIcon from "../../icons/trashR.svg";

const Families = () => {
  const [moduleData, setModuleData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [showModule, setShowModule] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [viewMode, setViewMode] = useState("tree");
  const [, setSearchName] = useState("");
  const [filterValues, setFilterValues] = useState({ viewMode: "tree", searchName: "" });
  const [parentMapping, setParentMapping] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const topRef = useRef(null);

  const { state: { role } } = useContext(UserContext);
  const history = useHistory();
  const { t } = useTranslation();

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const fetchData = useCallback(async  (signal) => {
    setLoading(true);
  
    try {
      const response = await secureGet("/families", showSnackbar, { signal });
      const mapping = {};
      response.data.forEach((item) => (mapping[item.id] = item.name));
  
      setParentMapping(mapping);
      setModuleData(response.data || []);
      setFilteredData(response.data || []);
      setTotalPages(Math.ceil((response.data || []).length / pageSize));
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching data:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [pageSize]);
  
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
  
    fetchData(signal);
  
    return () => {
      controller.abort();
    };
  }, [fetchData]);
  
  useEffect(() => 
    setTotalPages(Math.ceil(filteredData.length / pageSize)), 
  [pageSize, filteredData]);

  const handleAddClick = () => {
    setSelectedFamily(null);
    setShowModule(true);
  };

  const handleEditClick = (family) => {
    setSelectedFamily(family);
    setShowModule(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      const deleteUrl = `/families/delete/${itemToDelete.id}`;
      await secureDelete(deleteUrl, showSnackbar);
      setModuleData(prevData => prevData.filter(item => item.id !== itemToDelete.id));
      setFilteredData(prevData => prevData.filter(item => item.id !== itemToDelete.id));
      showSnackbar(t(`confirmation.familyDelete`), 'success');
    } catch (error) {
      console.error("Error deleting item:", error);
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const handleModuleClose = (action = "cancel") => {
    if (action === "submit") fetchData();
    setShowModule(false);
    setSelectedFamily(null);
  };

  const handleSearchChange = (value) => {
    setSearchName(value);
    const filtered = moduleData.filter((item) => item.name.toLowerCase().includes(value.toLowerCase()));
    setFilteredData(filtered);
  };

  const handleFilterChange = (name, value) => {
    setFilterValues(prev => ({ ...prev, [name]: value }));
    if (name === "viewMode") setViewMode(value);
    else if (name === "searchName") handleSearchChange(value);
  };

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setPageNumber(page);
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setTotalPages(Math.ceil(filteredData.length / newPageSize));
    setPageNumber(0);
  };

  const buildTree = (data) => {
    const map = {};
    data.forEach(item => map[item.id] = { ...item, children: [] });
    const tree = [];
    data.forEach(item => {
      if (item.parentId) {
        map[item.parentId]?.children.push(map[item.id]);
      } else {
        tree.push(map[item.id]);
      }
    });
    return tree;
  };

  const renderTree = (nodes, level = 0) => {
    const CARD_HEIGHT = 85;
    const CARD_MARGIN_BOTTOM = 24;
    const FULL_CHILD_HEIGHT = CARD_HEIGHT + CARD_MARGIN_BOTTOM;
    const CONNECTOR_DOT_OFFSET = CARD_HEIGHT / 2;

    const calculateVerticalHeight = (node) => {
      if (!node.children || node.children.length === 0) return 0;
      let totalHeight = (node.children.length - 1) * FULL_CHILD_HEIGHT;
      for (const child of node.children) {
        totalHeight += calculateVerticalHeight(child);
      }
      return totalHeight;
    };

    return nodes.map((node) => {
      const verticalHeight = calculateVerticalHeight(node);
      return (
        <div key={node.id} className="relative flex items-start">
          <div className="relative flex items-center mb-6">
            <FamilyCard
              name={node.name}
              image={node.image}
              width="350px"
              parentName={parentMapping[node.parentId] || t(`fields.families.noparent`)}
              onClick={() => history.push('/catalog/products', { familyId: node.id })}
              optionsMenu={<div onClick={(e) => e.stopPropagation()}>{role === 'ROLE_ADMIN' && <OptionsMenu options={getOptions(node)} />}</div>}
            />
            {node.children.length > 0 && (
              <div className="absolute left-[calc(100%+10px)] top-1/2 h-[2px] w-10 bg-gray-300">
                <div className="absolute -left-[4px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#043F88]" />
                <div className="absolute -right-[5px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#043F88] z-10" />
              </div>
            )}
          </div>
          {node.children.length > 0 && (
            <div className="relative flex flex-col items-start ml-16">
              {node.children.length > 1 && (
                <div className="absolute w-[2px] bg-gray-300" style={{ top: `${CONNECTOR_DOT_OFFSET}px`, left: '-13px', height: `${verticalHeight}px` }} />
              )}
              {node.children.map((child, childIndex) => (
                <div key={child.id} className="relative flex items-start w-full">
                  {childIndex !== 0 && (
                    <div className="absolute left-[-16px]" style={{ top: `${CONNECTOR_DOT_OFFSET}px`, transform: 'translateY(-50%)' }}>
                      <div className="w-2 h-2 rounded-full bg-[#043F88] z-10" />
                    </div>
                  )}
                  <div className="w-full">{renderTree([child], level + 1)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    });
  };

  const getOptions = (item) => [
    { label: t(`actions.modify`), icon: EditIcon, action: () => handleEditClick(item) },
    { label: t(`actions.delete`), color: "#EF476F", icon: DeleteIcon, action: () => handleDeleteClick(item) },
  ];

  const paginatedData = filteredData.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.families")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.families`)}</h1>
        <div className="ml-auto text-[16px]">
          {role === "ROLE_ADMIN" && 
          <PrimaryButton 
            label={t(`primaryButtons.add_family`)} 
            icon={AddIcon} 
            onClick={handleAddClick} 
          />}
        </div>
      </div>
      <Filter
        filters={[
          { name: "searchName", type: "search" },
          { name: "viewMode", label: t(`filters.families.view`), type: "dropdown", options: [{ value: "tree", label: t(`fields.families.tree`)}, { value: "list", label: t(`fields.families.list`) }] },
        ]}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
      />
      <div className="relative">
        {loading && <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex justify-center items-center z-10 pt-20"><LoadingLogo /></div>}
        {viewMode === "tree" ? (
          <div className="mt-6 overflow-x-auto" style={{ whiteSpace: "nowrap" }}>
            <div className="inline-block min-w-max">
              {renderTree(buildTree(moduleData))}
            </div>
          </div>
        ) : (
          <div 
            className={`mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 transition duration-300 ${loading ? "blur-sm pointer-events-none" : ""}`} 
            style={{ minHeight: "200px" }}
          >
            {paginatedData.map((item, index) => (
              <FamilyCard 
                key={index} 
                name={item.name} 
                image={item.image} 
                parentName={parentMapping[item.parentId] || t(`fields.families.noparent`)}
                onClick={() => history.push('/catalog/products', { familyId: item.id })}
                optionsMenu={
                  <div 
                    onClick={(e) => e.stopPropagation()}
                  >
                    {role === "ROLE_ADMIN" && 
                    <OptionsMenu options={getOptions(item)} 
                    />}
                  </div>}
              />
            ))}
          </div>
        )}
      </div>

      {viewMode === "list" && 
        <PageScroller 
          pageNumber={pageNumber} 
          totalPages={totalPages} 
          onPageChange={handlePageChange} 
          pageSize={pageSize} 
          onPageSizeChange={handlePageSizeChange} 
        />}

      {isDeletePopupVisible && 
        <DeletePopup 
          open={isDeletePopupVisible} 
          onCancel={() => setDeletePopupVisible(false)} 
          onConfirm={handleDeleteConfirm} 
        />}

      {showModule && 
        <ModuleWrapper 
          moduleType="families" 
          mode={selectedFamily ? "modify" : "add"} 
          onClose={handleModuleClose} 
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedFamily || {}} 
        />}

      <SnackbarNotification 
        open={snackbar.open} 
        message={snackbar.message} 
        severity={snackbar.severity} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Families;
